import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface JobResult {
  success: boolean;
  scheduled?: number;
  processed?: number;
  sent?: number;
  failed?: number;
  error?: string;
}

export interface JobConfig {
  name: string;
  functionName: string;
  description: string;
  icon: string;
}

export const EMAIL_JOBS: JobConfig[] = [
  {
    name: 'Processar Emails Agendados',
    functionName: 'process-scheduled-emails',
    description: 'Processa emails pendentes na fila de envio',
    icon: 'mail',
  },
  {
    name: 'Agendar Winback',
    functionName: 'schedule-winback-emails',
    description: 'Agenda emails de reengajamento para usuários inativos',
    icon: 'user-x',
  },
  {
    name: 'Agendar Digest Diário',
    functionName: 'schedule-daily-digest',
    description: 'Agenda resumo diário de atividades para usuários',
    icon: 'newspaper',
  },
  {
    name: 'Alertas de Assinatura',
    functionName: 'schedule-subscription-alerts',
    description: 'Agenda alertas de expiração de assinatura',
    icon: 'bell',
  },
];

export const useTriggerEmailJob = () => {
  const [loadingJobs, setLoadingJobs] = useState<Record<string, boolean>>({});
  const [lastResults, setLastResults] = useState<Record<string, JobResult | null>>({});

  const triggerJob = async (functionName: string): Promise<JobResult> => {
    setLoadingJobs(prev => ({ ...prev, [functionName]: true }));
    
    try {
      const { data, error } = await supabase.functions.invoke(functionName, {
        method: 'POST',
        body: {},
      });

      if (error) {
        throw error;
      }

      const result: JobResult = data || { success: true };
      setLastResults(prev => ({ ...prev, [functionName]: result }));

      // Log execution to cron_job_logs
      await supabase.from('cron_job_logs').insert({
        job_name: functionName,
        success: result.success,
        records_affected: result.scheduled || result.processed || result.sent || 0,
        error_message: result.error || null,
      });

      if (result.success) {
        const count = result.scheduled || result.processed || result.sent || 0;
        toast.success(`${functionName} executado com sucesso`, {
          description: `${count} registro(s) processado(s)`,
        });
      } else {
        toast.error(`Erro ao executar ${functionName}`, {
          description: result.error,
        });
      }

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      const result: JobResult = { success: false, error: errorMessage };
      setLastResults(prev => ({ ...prev, [functionName]: result }));

      // Log error
      await supabase.from('cron_job_logs').insert({
        job_name: functionName,
        success: false,
        error_message: errorMessage,
      });

      toast.error(`Erro ao executar ${functionName}`, {
        description: errorMessage,
      });

      return result;
    } finally {
      setLoadingJobs(prev => ({ ...prev, [functionName]: false }));
    }
  };

  const isLoading = (functionName: string) => loadingJobs[functionName] || false;
  const getLastResult = (functionName: string) => lastResults[functionName] || null;

  return {
    triggerJob,
    isLoading,
    getLastResult,
    EMAIL_JOBS,
  };
};
